import { APINames, getGlobal, TABBAR } from 'src/shared';
import { MacleApiError, MacleApiErrorno } from 'src/shared/api-error';
import { reporter } from 'src/shared/reporter';
import { IAnyStrObject, TabBar } from 'src/types';
import {
  NavigateToOption,
  RedirectToOption,
  SwitchTabOption,
  ReLaunchOption
} from './miniprogram';
import { ApiErrno, errnoCategory1, errnoCategory2, errorType } from 'src/shared/errno';

/**
 * fix route
 * @param currentRoute 当前路由
 * @param toUrl 目标 url
 */
export function getRealRoute(
  currentRoute: string,
  toUrl: string,
  isHtml: boolean = true
): string {
  let fixedUrl = toUrl;

  if (isHtml) {
    fixedUrl = addHTMLSuffix(fixedUrl);
  }

  if (fixedUrl.startsWith('/')) {
    return fixedUrl.substr('/'.length);
  }

  if (fixedUrl.startsWith('./')) {
    return getRealRoute(currentRoute, fixedUrl.substr('./'.length), false);
  }

  // 移除 url 前面的 ../ 并记录其位置
  let index;
  let urlArrLength;
  const urlArr = fixedUrl.split('/');
  for (
    index = 0, urlArrLength = urlArr.length;
    index < urlArrLength && urlArr[index] === '..';
    index++
  );
  urlArr.splice(0, index);
  const pathArr = currentRoute.length > 0 ? currentRoute.split('/') : [];
  pathArr.splice(pathArr.length - index - 1, index + 1);
  return pathArr.concat(urlArr).join('/');
}

export function encodeUrlQuery(url: string): string {
  const urlArr = url.split('?');
  const [urlPath, queryUrl] = urlArr;
  if (!queryUrl) {
    return url;
  }
  const queryParams = queryUrl.split('&').reduce((res: IAnyStrObject, cur) => {
    if (typeof cur === 'string' && cur.length > 0) {
      const curArr = cur.split('=');
      const key = curArr[0];
      const value = curArr[1];
      res[key] = value;
    }
    return res;
  }, Object.create(null));
  const urlQueryArr = [];
  for (const i in queryParams) {
    urlQueryArr.push(i + '=' + encodeURIComponent(queryParams[i]));
  }
  return urlQueryArr.length > 0 ? urlPath + '?' + urlQueryArr.join('&') : url;
}

export function addHTMLSuffix(url: string): string {
  const urlArr = url.split('?');
  urlArr[0] += '.html';
  return urlArr[1] === undefined ? urlArr[0] : `${urlArr[0]}?${urlArr[1]}`;
}

export function checkUrl(
  apiName: string,
  options:
    | NavigateToOption
    | RedirectToOption
    | SwitchTabOption
    | ReLaunchOption
): boolean {
  const tabBar = getGlobal(TABBAR) as TabBar | null;
  if (tabBar === null) {
    return true;
  }

  if (options.url.startsWith('/')) {
    options.url = options.url.slice('/'.length);
  }

  // 目标 url 是否在 tabbar 中
  const inTabBar = ((url: string) =>
    tabBar?.list.find(e => url.indexOf(e.pagePath) === 0))(options.url);

  // navigateTo 和 redirectTo API 禁止跳转到 tabbar 页面
  if (
    apiName === APINames.MiniProgram.NAVIGATE_TO ||
    apiName === APINames.MiniProgram.REDIRECT_TO
  ) {
    if (inTabBar) {
      reportErrorLog(
        apiName,
        `${apiName}: can not ${apiName} to a tabbar page`
      );
      return false;
    }
  }

  // switchTab 禁止跳转到非 tabbar 页面
  if (apiName === APINames.MiniProgram.SWITCH_TAB) {
    if (!inTabBar) {
      reportErrorLog(
        apiName,
        `${apiName}: can not ${apiName} to a non-tabbar page`
      );
      return false;
    }
  }
  return true;
}

export function validateUrl(url: string) {
  return /^(http|https):\/\/.*/i.test(url);
}

export function getDataType(data: any) {
  return Object.prototype.toString.call(data).split(' ')[1].split(']')[0];
}

export function reportErrorLog(apiName: string, reason: string) {
  const err = new MacleApiError(apiName, reason);
  reporter.normalError({
    message: err.message,
    file: err.fileName,
    line: err.lineNumber,
    column: err.columnNumber
  });
  return err;
}

/**
 * 对象元素都转成字符串
 * @param {Object} obj
 * @returns
 */
export function convertObjectValueToString(obj: IAnyStrObject) {
  const newObj: IAnyStrObject = {};
  for (const key in obj) {
    switch (getDataType(obj[key])) {
      case 'String':
        newObj[key] = obj[key];
        break;
      case 'Number':
        newObj[key] = `${obj[key]}`;
        break;
      case 'Boolean':
        newObj[key] = `${obj[key]}`;
        break;
      default:
        newObj[key] = JSON.stringify(obj[key]);
        break;
    }
  }
  return newObj;
}

/**
 * Get请求情况下，将参数拼接到url后
 * @param {String} url
 * @param {Object} data
 * @returns 把查询参数加到url后的值
 */
export function addQueryStringToUrl(url: string, data: Object) {
  if (isString(url) && isObject(data) && Object.keys(data).length) {
    const [host, query] = splitQuery(url);
    const oldParams = parseQuery(query);
    const encodeData = encodeDataFromObject(data);
    return `${host}?${urlEncodeFormData(extend(oldParams, encodeData))}`;
  }
  return url;
}

function splitQuery(url: string): string[] {
  return url.split('?');
}

function parseQuery(query: string): IAnyStrObject {
  return query.split('&').reduce((acc: IAnyStrObject, item) => {
    const [key, value] = item.split('=');
    acc[key] = value;
    return acc;
  }, {});
}

function encodeDataFromObject(data: Object): IAnyStrObject {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (isObject(value)) {
        return [key, JSON.stringify(value)];
      }
      return [key, value];
    })
  );
}

export function isArray(data: any) {
  return getDataType(data) === 'Array';
}

export function isString(data: any) {
  return 'String' === getDataType(data);
}

export function isObject(data: any) {
  return 'Object' === getDataType(data);
}

export function isNumber(data: any) {
  return getDataType(data) === 'Number';
}

/**
 * 将参数进行转码后拼接成String
 * @param {Object} data
 * @returns 参数进行转码后拼接成String
 */
export function urlEncodeFormData(data: any) {
  if (!isObject(data)) {
    return data;
  }
  const paramsArr = [];
  for (const key in data) {
    try {
      paramsArr.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`
      );
    } catch (error) {
      paramsArr.push(`${key}=${data[key]}`);
    }
  }
  return paramsArr.join('&');
}

/**
 * 将obj合并到target对象
 * @param {IAnyStrObject} target
 * @param {IAnyStrObject} obj
 * @returns 合并后的对象
 */
function extend(target: IAnyStrObject, obj: IAnyStrObject) {
  for (const [key, value] of Object.entries(obj)) {
    target[key] = value;
  }
  return target;
}

// 统一构造能上报file、line和column的日志
export function logErrorAndRunFail(
  apiName: string,
  params: IAnyStrObject,
  reason: string,
  errno?: number
) {
  const MacleApiError = new MacleApiErrorno(apiName, reason, errno);
  const errMsg = reason?.startsWith(apiName) ? reason : MacleApiError.message;
  reportErrorLog(apiName, reason);
  if (!params.success && !params.fail && !params.complete) {
    return new Promise((resolve, reject) => {
      params.fail = reject;
      params.fail({ 
        errMsg,
        ...(errno !== undefined ? { errno: MacleApiError.errno } : {})
       });
    });
  }
  for (const callback of ['fail', 'complete']) {
    const emptyFn: Function = function () {};
    params[callback]?.({ 
      errMsg,
      ...(errno !== undefined ? { errno: MacleApiError.errno } : {}) 
    }) || emptyFn;
  }
  return null;
}

export function paramCheck(
  apiName: string,
  params: IAnyStrObject,
  paramTpl: IAnyStrObject,
  name = 'parameter',
  required = false // 必填校验
) {
  let result = '';
  const pType = getDataType(params);
  const tplType = getDataType(paramTpl);
  if (pType !== tplType) {
    result = `${name} should be ${tplType} instead of ${pType};`;
    params.invalidParamObj = logErrorAndRunFail(apiName, params, result);
    return false;
  }
  if (tplType !== 'Object') {
    return true;
  }
  for (const [key, value] of Object.entries(paramTpl)) {
    if (Object.prototype.hasOwnProperty.call(params, key) || required) {
      const tplKeyType = getDataType(value);
      const pKeyType = getDataType(params[key]);
      if (tplKeyType !== pKeyType) {
        result += `${key} should be ${tplKeyType} instead of ${pKeyType};`;
      }
    }
  }
  if (!result) {
    return true;
  }
  params.invalidParamObj = logErrorAndRunFail(apiName, params, result);
  return false;
}

/**
 * 判断API部分必填参数的合法性，增加错误码版本的报错信息
 * @param {String} apiName
 * @param {Object} params
 * @param {Object} paramTpl
 * @param {String} name
 * @returns {boolean} 参数合法性
 */
export function paramCheckErrno(
  apiName: string,
  params: IAnyStrObject,
  paramTpl: IAnyStrObject,
  name = 'parameter',
  required = false // 必填校验
) {
  let result = '';
  let errno;
  const pType = getDataType(params);
  const tplType = getDataType(paramTpl);
  if (pType !== tplType) {
    result = `parameter error: parameter.${name} type not legal, please check it.`;
    params.invalidParamObj = logErrorAndRunFail(apiName, params, result, errno);
    return false;
  }
  if (tplType !== 'Object') {
    return true;
  }
  for (const [key, value] of Object.entries(paramTpl)) {
    if (Object.prototype.hasOwnProperty.call(params, key) || required) {
      const tplKeyType = getDataType(value);
      const pKeyType = getDataType(params[key]);
    if (tplKeyType !== pKeyType) {
        result = `parameter error: parameter.${key} type not legal, please check it.`;
        errno = ApiErrno.Errno(errnoCategory1.COMMON, errnoCategory2.COMMON, errorType.INVALID_TYPE);
        break;
      }
    }
  }
  if (!result) {
    return true;
  }
  params.invalidParamObj = logErrorAndRunFail(apiName, params, result, errno);
  return false;
}

/**
 * 进阶版的参数校验，覆盖常见的通用校验场景
 */
export function advanceParamCheck(
  apiName: string,
  params: IAnyStrObject,
  paramTpl: IAnyStrObject,
  options: IAnyStrObject = {},
  name = 'parameter'
) {
  const finalParamTpl = paramTplPreProcess(params, paramTpl, options); // 参数模板的前处理
  // 检查基本类型
  if (!paramCheck(apiName, params, finalParamTpl, name, true)) {
    return false;
  }
  return paramPostCheck(apiName, params, finalParamTpl, options, name);
}

/**
 * api参数等值的前处理
 */
function paramTplPreProcess(
  params: IAnyStrObject,
  paramTpl: IAnyStrObject,
  options: IAnyStrObject = {}
) {
  const finalParamTpl = { ...paramTpl };
  for (let key in paramTpl) {
    // 可选参数，在未定义时不做任何相关校验
    if (params[key] === undefined && options[key]?.optional) {
      delete finalParamTpl[key];
      delete options[key];
    }
  }
  return finalParamTpl;
}

function checkNonEmpty(key: string, value: any) {
  const isEmptyArr = isArray(value) && !value.length;
  const isEmptyStr = isString(value) && !value.trim();
  if (isEmptyArr || isEmptyStr) {
    return `parameter error: ${key} cannot be empty;`;
  }
  return '';
}

function checkSubElementType(key: string, value: any, tplValue: any) {
  let res = '';
  if (isArray(value)) {
    const validItemTypes = tplValue.map((itemTpl: any) => getDataType(itemTpl));
    const invalidItems = value.filter(
      (item: any) => !validItemTypes.includes(getDataType(item))
    );
    if (invalidItems.length) {
      res += `parameter error: invalid ${key} ${invalidItems}, the sub-element type of ${key} should be ${validItemTypes.join(
        ' or '
      )};`;
    }
  }
  if (isObject(value)) {
    for (const prop in tplValue) {
      const paramPropType = getDataType(value[prop]);
      const paramTplPropType = getDataType(tplValue[prop]);
      if (paramPropType !== paramTplPropType) {
        res += `parameter error: ${key}.${prop} should be ${paramTplPropType} instead of ${paramPropType};`;
      }
    }
  }
  return res;
}

function checkRange(key: string, value: any, range: any[]) {
  const items = isArray(value) ? value : [value];
  const invalidItems = items.filter((item: any) => !range.includes(item));
  if (invalidItems.length) {
    return `parameter error: invalid ${key} ${invalidItems};`;
  }
  return '';
}

/**
 * 参数后校验处理，判断空值等复杂场景
 */
function paramPostCheck(
  apiName: string,
  params: IAnyStrObject,
  paramTpl: IAnyStrObject,
  options: IAnyStrObject = {},
  name = 'parameter'
) {
  let result = '';

  for (let key in options) {
    const paramValue = params[key];
    const paramTplValue = paramTpl[key];
    const { range, checkSubEleType, nonempty } = options[key];

    if (nonempty) {
      result += checkNonEmpty(key, paramValue);
    }
    if (checkSubEleType) {
      result += checkSubElementType(key, paramValue, paramTplValue);
    }
    if (range?.length) {
      result += checkRange(key, paramValue, range);
    }
  }
  if (result) {
    params.invalidParamObj = logErrorAndRunFail(apiName, params, result);
    return false;
  }
  return true;
}

const supportedUserAuthScopes = [
  'AUTH_BASE',
  'AUTH_USER',
  'SEND_MESSAGE',
  'USER_NICKNAME',
  'USER_NAME',
  'USER_LOGIN_ID',
  'PLAINTEXT_USER_LOGIN_ID',
  'HASH_LOGIN_ID',
  'USER_AVATAR',
  'USER_GENDER',
  'USER_BIRTHDAY',
  'USER_NATIONALITY',
  'USER_CONTACTINFO',
  'USER_ADDRESS',
  'PLAINTEXT_MOBILE_PHONE'
];

const supportedCommonAuthScopes = [
  'location',
  'camera',
  'photosAlbums',
  'contacts',
  'record',
  'jumpMiniApp',
  'gotoBrowser',
  'bluetooth',
  'payment',
  'video',
  'storage'
];

export function authCodeScopesCheck(scopes: Array<string>) {
  return scopes.filter(
    scope => !supportedUserAuthScopes.includes(`${scope}`.toUpperCase())
  );
}

export function authScopesCheck(scopes: Array<string>) {
  const supportedAuthKeyScopes = [
    ...supportedCommonAuthScopes,
    ...supportedUserAuthScopes
  ];
  const supportedAuthScopes = supportedAuthKeyScopes.map(x => `scope.${x}`);
  return scopes.filter(scope => !supportedAuthScopes.includes(`${scope}`));
}

export function requiredParamsCheck(
  requiredParams: IAnyStrObject,
  params: IAnyStrObject,
  apiName: string
) {
  for (const [key, value] of Object.entries(requiredParams)) {
    // 仅支持null、undefined及过滤后空字符串的校验
    if (!value || (typeof value === 'string' && value.trim().length === 0)) {
      params.invalidParamObj = logErrorAndRunFail(
        apiName,
        params,
        `parameter error: ${key} should be required instead of Undefined or empty.`
      );
      return false;
    }
  }
  return true;
}

export function isUndefined(data: any) {
  return getDataType(data) === 'Undefined';
}

export function isFunction(listener: Function) {
  return getDataType(listener) === 'Function';
}

export function tranArrayBufferToUint8Array(data: ArrayBuffer) {
  return Array.from(new Uint8Array(data));
}

export function isValidBase64(base64String: string) {
  try {
    atob(base64String);
    return true;
  } catch (e) {
    return false;
  }
}
